import { addNotification } from './addNotification'
import { notifySessionExpired } from './notifySessionExpired'
import { logoutUser } from '~/services/users'
import type { IServiceError } from '~/services/errors'
import {
  AsyncOperationError,
  DeleteConflictError,
  NetworkError,
  NoOrderForOpportunityError,
  UpdateConflictError,
  ValidationError,
} from '~/services/errors'

/**
 * Shown for BOTH conflict paths: the async 202-poll one (`AsyncOperationError`
 * with errorType 'UpdateConflict') and the synchronous 409 one
 * (`UpdateConflictError`). Kept in one place so the two cannot drift — the
 * synchronous path had no message at all until 409 was given a type, and a
 * second hand-written copy is exactly how they would diverge.
 */
const UPDATE_CONFLICT_MESSAGE =
  'This record was changed by someone else.' +
  ' Refresh the page to see the current state and re-apply your edits.'

/**
 * Shown when a DELETE is refused because the row is still referenced.
 *
 * ⚠ DELIBERATELY NOT `UPDATE_CONFLICT_MESSAGE`, which is what a blocked delete
 * showed until 2026-09-04: *"This record was changed by someone else. Refresh
 * the page..."*. Nothing was changed by anyone and refreshing cannot help, so
 * that message sent a production user round the same failing delete three times
 * in 26 minutes. A message whose advice does not work is worse than none.
 *
 * Like `VALIDATION_REJECTED_MESSAGE`, it does NOT repeat the API's own wording:
 * the detail names a raw table (`order_ms_services`) and an entity id, which
 * belong in the console and the fault, not in front of a user. What the user
 * needs is the one fact the UI failed to convey — the record is in use.
 */
export const DELETE_CONFLICT_MESSAGE =
  'This record cannot be deleted while other records still refer to it.' +
  ' Remove or reassign those first.'

/**
 * Shown when the API rejects the payload itself (400/422 on a write).
 *
 * Deliberately does NOT repeat the API's own wording: `detail` names the
 * endpoint and `extra[].key` is a pydantic identifier (`split_fraction`), so
 * both go to the console and Honeybadger instead of in front of a user.
 */
export const VALIDATION_REJECTED_MESSAGE =
  'The server rejected this data.' +
  ' Check the values you entered and try again.'

export function isIServiceError(error: any): error is IServiceError {
  // A cast (`error as IServiceError`) is always !== undefined for any
  // non-undefined value, so the old check classified nearly everything —
  // including plain Errors and strings — as a service error. Discriminate
  // structurally instead: IServiceError carries a `statusCode` (number | null)
  // and a string `message`, which a plain Error does not have.
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof (error as IServiceError).message === 'string'
  )
}

function isError(error: any): error is Error {
  return error instanceof Error
}

/**
 * Reduce whatever an API handed us to a string the error page can display.
 *
 * `message` is not reliably text: it is sometimes the deserialised JSON error
 * body, e.g. `{ name, message, stack }`. Unwrap one level to that nested
 * `message` when present, and otherwise fall back to something legible rather
 * than letting it stringify to "[object Object]".
 */
export function toDisplayMessage(message: unknown): string {
  if (typeof message === 'string') {
    return message
  }
  if (message && typeof message === 'object') {
    const nested = (message as { message?: unknown }).message
    // An object with no usable message must NOT fall through to String(), which
    // is what produced "[object Object]" in the first place; and JSON-dumping it
    // is what Nuxt 2 did, stack and all. Say something plain instead.
    return typeof nested === 'string' ? nested : 'An error occurred'
  }
  if (message === null || message === undefined) {
    return 'An error occurred'
  }
  // A primitive (number, boolean, …) — safe to render as-is.
  return String(message)
}

export async function handleAsyncDataErrors({
  callingMn,
  error,
  context,
}: {
  callingMn: string
  error: unknown
  context: any
}) {
  const fn = `${callingMn} --> [cynqueModules/handleAsyncDataErrors] |`
  console.debug(fn)
  console.dir(error)

  // Checked BEFORE isIServiceError: NetworkError extends ServiceError, so it
  // satisfies that structural test and would otherwise fall through to the
  // `context.error()` at the end — replacing the whole SPA with an error page
  // for what is typically a momentary blip (a dyno restarting, a laptop
  // waking, a flaky connection). A notification says the same thing without
  // discarding the user's place in the app, and the request can be retried by
  // repeating whatever the user just did.
  if (error instanceof NetworkError) {
    console.debug(`${fn} NetworkError — ${error.method} ${error.url}`)
    addNotification({
      type: 'warning',
      message:
        'Could not reach the server. Check your connection and try again.',
    })
    return
  }

  // Checked BEFORE isIServiceError for the same reason as NetworkError above:
  // UpdateConflictError extends ServiceError, so it satisfies that structural
  // test and would otherwise fall through to `context.error()` — replacing the
  // whole SPA with an error page because someone else touched the record.
  if (error instanceof UpdateConflictError) {
    console.debug(`${fn} UpdateConflictError — ${error.method} ${error.url}`)
    console.debug(`${fn} conflict body:`, error.body)
    addNotification({ type: 'warning', message: UPDATE_CONFLICT_MESSAGE })
    return
  }

  // ⚠ MUST BE ITS OWN BRANCH, and must come before isIServiceError for the same
  // reason as the one above. A 409 is none of the statuses that block handles,
  // so a DeleteConflictError reaching it takes over the whole SPA with an error
  // page because a row was still referenced. Kept adjacent to the update branch
  // because the two are easy to conflate — and conflating them is precisely the
  // production defect this pair now prevents: a DELETE sends no version, so the
  // message above is never the right one for one.
  if (error instanceof DeleteConflictError) {
    console.debug(`${fn} DeleteConflictError — ${error.method} ${error.url}`)
    console.debug(`${fn} conflict body:`, error.body)
    addNotification({ type: 'warning', message: DELETE_CONFLICT_MESSAGE })
    return
  }

  // Checked BEFORE isIServiceError for the same reason as the two above:
  // ValidationError extends ServiceError, so it satisfies that structural test
  // and would otherwise fall straight through to the 403/422 branch below —
  // which LOGS THE USER OUT. That is not hypothetical: four components route a
  // failed PATCH through here (FieldSetFieldsCard, SupplierProductsCard,
  // SupplierProductItemsCard, OpportunityStagesCard), so a validation 422 on a
  // seq-num reorder ends the session today.
  //
  // ⚠ The 422-means-expired condition below is deliberately NOT narrowed. It is
  // genuinely correct on the READ path — httpGet has no 422 case, so a 422 there
  // falls to `default:` -> APIUnhandledStatus -> statusCode 422 -> logout, and
  // that has been the session-recovery path for years. Typing the write path so
  // it returns above that check fixes the reachable half without touching the
  // half we cannot test (nothing mocks a read 422). The same predicate is
  // duplicated as `isAuthFailure` in getSelectList.ts, whose comment names this
  // block as its source of truth — change one without the other and they drift.
  if (error instanceof ValidationError) {
    console.debug(`${fn} ValidationError — ${error.method} ${error.url}`)
    console.debug(`${fn} rejected body:`, error.body)
    addNotification({
      type: 'warning',
      message: VALIDATION_REJECTED_MESSAGE,
      statusCode: error.statusCode ?? undefined,
    })
    return
  }

  // Checked BEFORE isIServiceError for the same reason as the branches above:
  // NoOrderForOpportunityError extends ServiceError, so it satisfies that
  // structural test and would otherwise replace the whole SPA with an error page
  // reading "Opportunity 'XXO0001' has no order." — for a state that is normal.
  //
  // The Operations tab is hidden when the opportunity has no order, but a stored
  // `selectedScreenTopTabValue: 'Operations'`, a bookmark, or an order deleted in
  // another tab all still route here. Sales is where that user meant to be.
  //
  // ⚠ `context.redirect` is AWAITED. It looks synchronous and is not — an
  // unawaited one lets asyncData resolve first and the redirect is lost, which is
  // exactly how the 404 "record is gone" redirect regressed under Nuxt 4.
  if (error instanceof NoOrderForOpportunityError) {
    console.debug(`${fn} NoOrderForOpportunityError — ${error.opportunityId}`)
    await context.redirect({
      name: 'Opportunities-Overview-Sales-id',
      params: { id: error.opportunityId },
    })
    return
  }

  if (error instanceof AsyncOperationError) {
    console.debug(`${fn} AsyncOperationError — errorType: ${error.errorType}`)
    switch (error.errorType) {
      case 'EntityNotFound':
        addNotification({
          type: 'warning',
          message: 'The record was not found. It may have been deleted.',
        })
        return
      case 'UpdateConflict': {
        const detailsHint = error.details?.constraint_name
          ? ` (conflict: ${error.details.constraint_name})`
          : ''
        addNotification({
          type: 'warning',
          message: UPDATE_CONFLICT_MESSAGE + detailsHint,
        })
        return
      }
      case 'Timeout':
        addNotification({
          type: 'info',
          message:
            'The operation is still processing. It will complete in the background.',
        })
        return
      default:
        context.error({
          statusCode: 500,
          message: `A background operation failed: ${error.message}`,
        })
        return
    }
  }

  if (isIServiceError(error)) {
    console.debug(`${fn} isIServiceError`)
    console.debug('error:', error)
    if (error.statusCode === 403 || error.statusCode === 422) {
      notifySessionExpired(error.statusCode)
      logoutUser()
      return
    }
    if (error.statusCode === 404) {
      // This happens in mock data mode, when user has, say, created a
      // new company, is on the company overview screen for this new
      // company, and refreshes their browswer--which nukes the new
      // company and makes the overview page unrefreshable. This block
      // gracefully returns the user to the list screen for the entity.
      let redirectTo
      if (context.route.name?.includes('-id')) {
        if (context.route.name === 'Locations-id') {
          addNotification({
            type: 'warning',
            message: 'Could not load Location Overview',
            statusCode: error.statusCode,
          })
          // Two levels down. Don't have path to company originally associated
          // with the deleted location anymore.
          redirectTo = '/Companies'
        } else {
          const routeComponents = context.route.name.split('-')
          redirectTo = '/' + routeComponents[0].replaceAll(' ', '%20')
        }
      }
      if (redirectTo) {
        console.debug(`${fn} Redirecting to ${redirectTo}`)
        await context.redirect(redirectTo)
        return
      }
    }
    console.debug(`${fn} gonna display on Error page (or retry)`)
    // Coerce to a STRING. An API error's `message` is sometimes the deserialised
    // JSON error body ({name, message, stack}) rather than text, and the error
    // page renders it raw: Nuxt 4 stringifies it to a literal "[object Object]"
    // (Nuxt 2 dumped the whole blob, stack and all). Either way the one thing the
    // page exists to tell you — WHAT went wrong — was unreadable.
    const theErrorMessage = toDisplayMessage(
      error.error ? error.error.message : error.message
    )
    const theErrorCode = error.error?.statusCode
      ? error.error.statusCode
      : error.statusCode
      ? error.statusCode
      : undefined
    // addNotification({
    //   type: 'warning',
    //   message: theErrorMessage,
    //   statusCode: theErrorCode,
    //   store: context.store,
    // })
    context.error({
      statusCode: theErrorCode,
      message: theErrorMessage,
    })
    return
  }

  if (isError(error)) {
    console.debug(`${fn} isJavascriptError`)
    context.error({
      message: error.message,
    })
    return
  }

  console.debug(`${fn} is unknown kind of error`)
  context.error({
    message: 'An uknown kind of error has occured. Please call tech support.',
  })
}
